import { Box, Divider, Typography } from '@mui/material';
import { Drone, droneLabel } from '../../types/drone';
import { useNow } from '../../hooks/useNow';
import { isTelemetryStale } from '../../utils/staleness';
import { formatBatteryPercentage } from '../../utils/battery';
import { formatRelativeTime, formatTimestamp } from '../../utils/formatters';
import { renewCommands } from '../../utils/credentials';
import { DroneImage } from '../DronePanel/DroneImage';
import { DroneNameEditor } from '../DronePanel/DroneNameEditor';
import { DroneStatusChips } from '../DronePanel/DroneStatusChips';
import { DroneRegistrationSection } from './DroneRegistrationSection';
import { CredentialsChip } from './CredentialsChip';
import { VirtualDroneSoftwareSection } from './VirtualDroneSoftwareSection';

const Row: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({
  label,
  children,
  mono = false,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ overflowWrap: 'anywhere', ...(mono && { fontFamily: 'monospace' }) }}
    >
      {children}
    </Typography>
  </Box>
);

/**
 * When the drone's credentials expire, or that they no longer work, with how
 * to renew them (#777, C16). Each refresh moves the expiry on, 180 days for a
 * physical drone and 30 for a virtual one (#808).
 */
const describeCredentials = (drone: Drone): string => {
  const c = drone.credentials;
  if (!c) return '';
  const renew = `Renew with ${renewCommands(drone).join(', or in the cluster ')}.`;
  switch (c.state) {
    case 'revoked':
      return `Revoked: a refresh token was used twice. ${renew}`;
    case 'expired':
      return `Expired ${formatTimestamp(c.expiresAt)}. ${renew}`;
    case 'valid':
      return Date.parse(c.expiresAt) <= Date.now()
        ? `Expired ${formatTimestamp(c.expiresAt)}. ${renew}`
        : `Valid until ${formatTimestamp(c.expiresAt)}`;
  }
};

/**
 * One drone as a report (#411): what it is called, what it is, and how it was
 * last heard from.
 *
 * Shows only what the API sends (G9). Voltage and motor RPM join with #409 and
 * the photo with #408; until then there is no row for them, rather than a "—"
 * for something the system cannot report yet.
 */
export const DroneReportCard: React.FC<{ drone: Drone }> = ({ drone }) => {
  const now = useNow();
  const stale = isTelemetryStale(drone.lastUpdateTime, now);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DroneImage imageUrl={null} droneName={droneLabel(drone)} height={180} />
      <DroneNameEditor drone={drone} variant="h5" />
      <DroneStatusChips drone={drone} />
      <CredentialsChip drone={drone} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Row label="ID" mono>
          {drone.id}
        </Row>
        <Row label="Serial" mono>
          {drone.hardwareId ?? 'Not reported'}
        </Row>
        <Row label="Model">{drone.model ?? 'Not recorded'}</Row>
        <Row label="Software" mono>
          {drone.softwareVersion ?? 'Not reported'}
        </Row>
        <Row label="Battery">{formatBatteryPercentage(drone.battery.percentage)}</Row>
        <Row label={stale ? 'Last seen' : 'Last update'}>
          {drone.lastUpdateTime ? formatRelativeTime(drone.lastUpdateTime) : 'Never'}
        </Row>
        {drone.credentials && (
          <Row label="Credentials">{describeCredentials(drone)}</Row>
        )}
      </Box>

      <Divider />
      {drone.virtual ? <VirtualDroneSoftwareSection drone={drone} /> : <DroneRegistrationSection drone={drone} />}
    </Box>
  );
};
